import type { GigState, BookingState } from './types.js';

export type LifecycleTransition = {
  from: GigState;
  to: GigState;
  guard?: (context: unknown) => boolean;
};

const GIG_TRANSITIONS: LifecycleTransition[] = [
  // DRAFT → QUOTED (quote prepared)
  { from: 'DRAFT', to: 'QUOTED' },

  // QUOTED → BOOKED (all positions filled or management decides to proceed)
  { from: 'QUOTED', to: 'BOOKED' },

  // QUOTED → DRAFT (go back to edit)
  { from: 'QUOTED', to: 'DRAFT' },

  // BOOKED → COMPLETED (gig finished, payroll generated)
  { from: 'BOOKED', to: 'COMPLETED' },

  // COMPLETED → INVOICED (invoice sent to client)
  { from: 'COMPLETED', to: 'INVOICED' },

  // INVOICED → PAID (payment received)
  { from: 'INVOICED', to: 'PAID' },

  // Any state → CANCELLED (can cancel at any point)
  { from: 'DRAFT', to: 'CANCELLED' },
  { from: 'QUOTED', to: 'CANCELLED' },
  { from: 'BOOKED', to: 'CANCELLED' },
  { from: 'COMPLETED', to: 'CANCELLED' },
];

export function isValidTransition(from: GigState, to: GigState): boolean {
  return GIG_TRANSITIONS.some(t => t.from === from && t.to === to);
}

export function getValidNextStates(currentState: GigState): GigState[] {
  return GIG_TRANSITIONS.filter(t => t.from === currentState).map(t => t.to);
}

export function getStateDescription(state: GigState): string {
  const descriptions: Record<GigState, string> = {
    DRAFT: 'Gig is being prepared',
    QUOTED: 'Quote has been provided to client',
    BOOKED: 'Gig is confirmed and ready',
    COMPLETED: 'Gig has been completed, payroll generated',
    INVOICED: 'Invoice has been sent to client',
    PAID: 'Payment received',
    CANCELLED: 'Gig has been cancelled',
  };
  return descriptions[state];
}

// Booking transitions — client-side guard only. Cloud Functions react to
// whatever transition actually happened rather than re-validating it (no
// runtime @gigdriver/domain dependency there), so this doesn't need a
// server-side mirror the way ot-cost/budget math does.
type BookingLifecycleTransition = { from: BookingState; to: BookingState };

const BOOKING_TRANSITIONS: BookingLifecycleTransition[] = [
  { from: 'DRAFT', to: 'OFFERED' },
  // OFFERED → CANCELLED is "pull the offer" — no calendar invite was ever
  // sent for it (that only happens on ACCEPT), so cancelling it is cheap,
  // but it still deserves a real record instead of vanishing.
  { from: 'OFFERED', to: 'ACCEPTED' },
  { from: 'OFFERED', to: 'DECLINED' },
  { from: 'OFFERED', to: 'CANCELLED' },
  // ACCEPTED/BOOKED → CANCELLED is the real "release someone after they
  // committed" path — see cancelBooking. DRAFT → CANCELLED is deliberately
  // absent: a draft assignment has no history worth keeping, so removing
  // one stays a hard delete via unassignPerson instead.
  { from: 'ACCEPTED', to: 'CANCELLED' },
  { from: 'ACCEPTED', to: 'BOOKED' },
  { from: 'BOOKED', to: 'CANCELLED' },
  { from: 'BOOKED', to: 'COMPLETED' },
  { from: 'COMPLETED', to: 'INVOICED' },
];

export function isValidBookingTransition(from: BookingState, to: BookingState): boolean {
  return BOOKING_TRANSITIONS.some(t => t.from === from && t.to === to);
}

export function getValidNextBookingStates(from: BookingState): BookingState[] {
  return BOOKING_TRANSITIONS.filter(t => t.from === from).map(t => t.to);
}
